<template>
  <AppCartSidebarPanel
    :open="showCartSidebar"
    :item-count="totalCartQuantity"
    :subtotal="cartSubtotal"
    :has-items="cartHasBundles"
    :announcement="announcement"
    @close="handleSidebarCloseBtnClick"
    @interact="settleOnInteraction"
  >
    <template #tracker>
      <RewardsTrackerFlyout
        class="cart-sidebar__tracker"
        :fill-percent="trackerFillPercent"
        :fill-transition="fillTransition"
        :celebrating-tier-id="celebratingTierId"
        :celebration-stage="celebrationStage"
        :held-tier-ids="heldTierIds"
      />
    </template>
    <template #callout>
      <FreeGiftCallout
        v-if="freeGiftCallout"
        :heading-html="freeGiftCallout.headingHtml"
        :reward-name="freeGiftCallout.rewardName"
        :detail-image-url="freeGiftCallout.detailImageUrl"
        :unlocked-reward-name="freeGiftCallout.unlockedRewardName"
        :unlocked-detail-image-url="freeGiftCallout.unlockedDetailImageUrl"
        :is-unlocked="isFreeGiftUnlocked"
      />
    </template>
    <template #items>
      <AppCartSidebarBundleItem
        v-for="cartBundle in visibleCartBundles"
        :key="cartBundle.cartBundleId"
        :cart-bundle="cartBundle"
        :is-arriving="bundleIsArriving(cartBundle)"
      />
      <AppCartSidebarSynthesizedRewardCartItem
        :withheld-tier-ids="withheldRewardTierIds"
        :arriving-tier-ids="arrivingRewardTierIds"
      />
    </template>
    <template #quickAdds>
      <QuickAddsFlyout />
    </template>
  </AppCartSidebarPanel>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useCartStore } from '@/stores/cart'
import { useMainStore } from '@/stores/main'
import { mapState, mapStores } from 'pinia'

import { useGtmTracking } from '@/composables/gtmTracking'
import { useCartFlyoutSequence } from '@/composables/useCartFlyoutSequence'

import FreeGiftCallout from '@/components/Cart/FreeGift/FreeGiftCallout.vue'
import QuickAddsFlyout from '@/components/Cart/QuickAdds/QuickAddsFlyout.vue'
import RewardsTrackerFlyout from '@/components/Shared/RewardsTracker/RewardsTrackerFlyout.vue'

import type { CartBundleStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import AppCartSidebarBundleItem from './AppCartSidebarBundleItem.vue'
import AppCartSidebarPanel from './AppCartSidebarPanel.vue'
import AppCartSidebarSynthesizedRewardCartItem from './AppCartSidebarSynthesizedRewardCartItem.vue'

// Set on `<body>` while the flyout is open. Lets global CSS suppress page chrome that out-stacks the
// scrim — see `main.scss`. A class rather than a store read keeps the affected components unaware of
// the cart. It stays in this wrapper rather than moving into the panel: it is a statement about the
// *page* behind the drawer, and the debug harness — which renders the same panel on its own page —
// must not have its own scrolling suppressed.
const BODY_OPEN_CLASS = 'cart-flyout-open'

/** GA's standard cart-view event, kept for opens the customer asked for. */
const VIEW_CART_EVENT = 'view_cart'
/**
 * The same event with an `_auto` suffix, for opens the unlock sequence performed (Gap 18, name decided
 * 2026-08-05). Registering it GA-side is an Analytics task and does not block this.
 */
const AUTO_OPEN_EVENT = 'view_cart_auto'

/**
 * Cart-flyout placement: binds the presentational `AppCartSidebarPanel` to the cart, and fills its
 * slots with the store-bound tracker / callout / rows / Quick Adds. Every store read for the flyout
 * lives here or in one of those children; the panel itself reads nothing, which is what lets the debug
 * harness (Gap 13) drive the same panel from fixtures.
 *
 * **It also hosts the unlock sequence** (A2b). One run drives the tracker's fill and nodes, the
 * banner's crossfade and the reward rows' arrival, so exactly one component may own it — and this is
 * the only one mounted on every route with all three in scope. `useCartFlyoutSequence` holds the
 * machine and its cart wiring; everything below is the run rendered.
 */
export default defineComponent({
  name: 'AppCartSidebar',
  setup() {
    return {
      ...useGtmTracking(),
      ...useCartFlyoutSequence()
    }
  },
  components: {
    AppCartSidebarPanel,
    AppCartSidebarBundleItem,
    AppCartSidebarSynthesizedRewardCartItem,
    RewardsTrackerFlyout,
    QuickAddsFlyout,
    FreeGiftCallout
  },
  computed: {
    ...mapStores(useMainStore),
    ...mapState(useMainStore, ['showCartSidebar']),
    ...mapState(useCartStore, [
      'cartData',
      'cartSubtotal',
      'cartHasBundles',
      'cartBundlesAsGoogleAnalyticsCartItems',
      'totalCartQuantity'
    ]),
    /**
     * The cart's bundles, less any reward row the running sequence is still holding back.
     *
     * The row's data arrived with the cart response; withholding it is what lets the 4,000ms beat
     * reveal it. Keyed on `sourcePromotionId` — the same id space as the ladder's `promotionId` — so a
     * reward granted by an *earlier* mutation is never withheld, only the ones this run unlocked.
     */
    visibleCartBundles(): CartBundleStorefrontDto[] {
      const cartBundles = this.cartData?.cartBundles ?? []
      if (this.withheldRewardTierIds.length === 0) return cartBundles

      return cartBundles.filter(
        (cartBundle) =>
          !cartBundle.sourcePromotionId ||
          !this.withheldRewardTierIds.includes(cartBundle.sourcePromotionId)
      )
    }
  },
  methods: {
    handleSidebarCloseBtnClick() {
      this.mainStore.showCartSidebar = false
    },
    /** True for the rows the reveal beat just brought in, which is what plays the arrival flash. */
    bundleIsArriving(cartBundle: CartBundleStorefrontDto): boolean {
      return (
        !!cartBundle.sourcePromotionId &&
        this.arrivingRewardTierIds.includes(cartBundle.sourcePromotionId)
      )
    }
  },
  watch: {
    showCartSidebar(newValue) {
      document.body.classList.toggle(BODY_OPEN_CLASS, newValue)

      if (newValue) {
        // Same payload, two names (Gap 18). Auto-opening on every add would roughly double `view_cart`
        // — a cart view is logged every time the panel opens — so the automatic ones are separable
        // while staying comparable against organic opens. The cause comes from the sequence, because
        // this watcher can see *that* the panel opened and not *why*.
        this.trackGtmEvent(this.consumeAutoOpen() ? AUTO_OPEN_EVENT : VIEW_CART_EVENT, {
          ecommerce: {
            items: this.cartBundlesAsGoogleAnalyticsCartItems
          }
        })
      }
    }
  },
  mounted() {
    // The watcher below cannot carry this on its own. `showCartSidebar` is persisted, so a reload
    // with the flyout open rehydrates it as `true` and the panel renders open without the value ever
    // *changing* — leaving the page behind scrolling under the scrim, and `BackToTop` lit and
    // clickable over a dimmed page. Applied here as well so the class tracks the rendered state
    // rather than only transitions into it.
    document.body.classList.toggle(BODY_OPEN_CLASS, this.showCartSidebar)
  },
  beforeUnmount() {
    document.body.classList.remove(BODY_OPEN_CLASS)
  }
})
</script>
